import { useState, useEffect } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  ArrowPathIcon,
  CircleStackIcon,
  KeyIcon,
  TrashIcon,
  CogIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  Bars3Icon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { cacheApi } from '../api/endpoints';
import { KeyDistributionChart } from '../components/charts/KeyDistributionChart';
import { useCacheStore } from '../stores/cacheStore';
import Listbox from '../components/ui/Listbox';
import { Button } from '../components/ui/Button';
import { KeyTree } from '../components/cache/KeyTree';
import { KeyDetailPanel } from '../components/cache/KeyDetailPanel';
import SpotlightCard from '../components/ui/SpotlightCard';
import { InteractiveList } from '../components/ui/PhysicsList';
import PageHeader from '../components/ui/PageHeader';
import { CardSkeleton, StatsGridSkeleton, ListSkeleton } from '../components/ui/SkeletonPatterns';

interface CacheStats {
  overview: {
    provider: string;
    configuredProvider: string;
    capabilities: {
      canListKeys: boolean;
      canWrite: boolean;
    };
    lastError: string | null;
  };
  counts: {
    totalKeys: number;
    instanceKeys: number;
    taskKeys: number;
    sessionKeys: number;
  };
  samples: {
    allKeys: string[];
    instanceKeys: string[];
    taskKeys: string[];
    sessionKeys: string[];
  };
  config: {
    listLimit: number;
    defaultTtl: number;
  };
}

export default function CacheManagement() {
  const { 
    providers, 
    selectedProvider, 
    fetchProviders, 
    setSelectedProvider,
    currentProviderName 
  } = useCacheStore();

  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [deletePattern, setDeletePattern] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
  const cacheDuration = 5 * 60 * 1000; // 5 minutes

  const isCacheValid = () => {
    if (!lastFetchTime) return false;
    return Date.now() - lastFetchTime < cacheDuration;
  };

  const handleViewDetails = (key: string) => {
    if (selectedKey === key && showDetailPanel) {
      setShowDetailPanel(false);
      setSelectedKey(null);
    } else {
      setSelectedKey(key);
      setShowDetailPanel(true);
    }
  };

  const loadCacheStats = async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid() && stats) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await cacheApi.getStats(selectedProvider || undefined);
      if (response.success && response.data) {
        setStats(response.data as CacheStats);
        setLastFetchTime(Date.now());
      } else {
        throw new Error(response.error?.message || 'Failed to load cache stats');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cache stats');
      console.error('Cache stats load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteKeys = async (keys: string[]) => {
    try {
      const response = await cacheApi.deleteKeys({ keys }, selectedProvider || undefined);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete keys');
      }
      setSelectedKeys([]);
      await loadCacheStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete keys');
    }
  };

  const deleteByPattern = async (pattern: string) => {
    try {
      const response = await cacheApi.deleteKeys({ pattern }, selectedProvider || undefined);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete keys by pattern');
      }
      setDeletePattern('');
      await loadCacheStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete keys by pattern');
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Ensure we check that all cards have physics enabled
  // This is a side-effect to ensure consistency, though typically props control this directly
  
  useEffect(() => {
    const cacheIsValid = isCacheValid();
    const hasCachedData = stats !== null;

    if (cacheIsValid && hasCachedData) {
      setLoading(false);
      return;
    }

    loadCacheStats();
  }, [selectedProvider]); // Reload stats when provider changes

  const getStatusBadge = (_provider: string, lastError: string | null) => {
    if (lastError) {
      return (
        <span className="inline-flex items-center rounded-md bg-red-500/[8%] px-2 py-0.5 text-xs font-medium text-red-400">
          <ExclamationTriangleIcon className="mr-1 h-3 w-3" />
          Error
        </span>
      );
    }
    return (
        <span className="inline-flex items-center rounded-md bg-green-500/[8%] px-2 py-0.5 text-xs font-medium text-green-400">
          <CheckIcon className="mr-1 h-3 w-3" />
        Healthy
      </span>
    );
  };

  const formatTTL = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };
  
  const providerOptions = [
    { value: '', label: `默认 (${currentProviderName || 'Auto'})` },
    ...providers.map(p => ({ value: p.name, label: `${p.name} (${p.type})` }))
  ];

  return (
    <div className="flex w-full overflow-hidden">
        <div className="flex-1 min-w-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
          {/* Header */}
            <div className="mb-8">
              <PageHeader
                title="缓存管理"
                subtitle="监控和管理缓存系统"
                actions={
                  <div className="flex items-center gap-4">
                    <div className="w-64">
                      <Listbox
                        label="选择 Provider"
                        options={providerOptions}
                        value={selectedProvider || ''}
                        onChange={(val) => setSelectedProvider(val || null)}
                      />
                    </div>
                    <Button variant="default" onClick={() => loadCacheStats(true)} disabled={loading}>
                      <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      刷新
                    </Button>
                  </div>
                }
              />
            </div>

          {/* Error Display */}
          {error && (
            <div className="mb-8 rounded-2xl bg-red-500/10 p-4 shadow-inner shadow-red-500/20">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-200">加载失败</h3>
                  <div className="mt-2 text-sm text-red-300">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && !stats ? (
            <div className="space-y-6">
              <StatsGridSkeleton count={3} />
              <CardSkeleton />
              <ListSkeleton items={5} />
            </div>
          ) : (
            <TabGroup selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex} as="div" className="w-full">
              <div className="flex justify-center mb-10">
              <div className="relative p-1 rounded-full bg-white/5 inline-flex overflow-hidden">
                {/* Sliding Indicator */}
                <div 
                  className="absolute top-1 bottom-1 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] bg-white/10 rounded-full"
                  style={{
                      width: 'calc(33.333% - 4px)', 
                      left: '2px',
                      transform: `translateX(${selectedTabIndex * 100}%)` 
                    }}
                  />
                  
                  <TabList className="flex relative z-10">
                    {['概览', '键管理', '操作'].map((item) => (
                      <Tab
                        key={item}
                        className="px-6 py-2 rounded-full text-sm font-medium leading-5 outline-none transition-all duration-300 text-gray-400 data-[selected]:text-white hover:text-white/80 group whitespace-nowrap"
                      >
                        {item}
                      </Tab>
                    ))}
                  </TabList>
                </div>
              </div>

              <TabPanels as="div">
                {/* Overview Tab */}
                <TabPanel className="outline-none transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] data-closed:opacity-0 data-closed:translate-y-4">

                {stats && (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {/* Provider Info */}
                    <SpotlightCard variant="flat" className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-sky-400">
                            <CircleStackIcon className="h-6 w-6" />
                          </div>
                          <h3 className="ml-4 text-lg font-medium text-white">
                            Cache Provider
                          </h3>
                        </div>
                        <dl className="space-y-3">
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Provider:</dt>
                            <dd className="text-sm text-white">{stats.overview.provider}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Configured:</dt>
                            <dd className="text-sm text-white">{stats.overview.configuredProvider}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Status:</dt>
                            <dd>{getStatusBadge(stats.overview.provider, stats.overview.lastError)}</dd>
                          </div>
                        </dl>
                      </div>
                    </SpotlightCard>

                    {/* Key Counts */}
                    <SpotlightCard variant="flat" className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
                            <KeyIcon className="h-6 w-6" />
                          </div>
                          <h3 className="ml-4 text-lg font-medium text-white">
                            Key Counts
                          </h3>
                        </div>
                        <dl className="space-y-3">
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Total Keys:</dt>
                            <dd className="text-sm font-mono text-white">{stats.counts.totalKeys}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Instance:</dt>
                            <dd className="text-sm font-mono text-white">{stats.counts.instanceKeys}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Task:</dt>
                            <dd className="text-sm font-mono text-white">{stats.counts.taskKeys}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Session:</dt>
                            <dd className="text-sm font-mono text-white">{stats.counts.sessionKeys}</dd>
                          </div>
                        </dl>
                      </div>
                    </SpotlightCard>

                    {/* Configuration */}
                    <SpotlightCard variant="flat" className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-400">
                            <CogIcon className="h-6 w-6" />
                          </div>
                          <h3 className="ml-4 text-lg font-medium text-white">
                            Configuration
                          </h3>
                        </div>
                        <dl className="space-y-3">
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">List Limit:</dt>
                            <dd className="text-sm font-mono text-white">{stats.config.listLimit}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Default TTL:</dt>
                            <dd className="text-sm font-mono text-white">{formatTTL(stats.config.defaultTtl)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Can List Keys:</dt>
                            <dd className="text-sm text-green-400">{stats.overview.capabilities.canListKeys ? 'Yes' : 'No'}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-400">Can Write:</dt>
                            <dd className="text-sm text-green-400">{stats.overview.capabilities.canWrite ? 'Yes' : 'No'}</dd>
                          </div>
                        </dl>
                      </div>
                    </SpotlightCard>
                  </div>
                )}
                </TabPanel>


              {/* Key Management Tab */}
              <TabPanel className="outline-none transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] data-closed:opacity-0 data-closed:translate-y-4">
                {stats && (
                  <SpotlightCard className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                    <div className="p-6">
                      <div className="flex items-end justify-between mb-8">
                        <div>
                          <h3 className="text-lg font-medium text-white">Key Management</h3>
                          <p className="text-sm text-white/40 mt-1">管理和清理缓存键</p>
                        </div>
                        <div className="flex items-end space-x-6">
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 mb-2 px-1">
                              视图模式
                            </label>
                            <div className="relative p-1 rounded-xl bg-white/5 inline-flex overflow-hidden">
                              <div 
                                className="absolute top-1 bottom-1 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] bg-white/10 rounded-xl"
                                style={{ 
                                  width: 'calc(50% - 4px)', 
                                  left: '2px',
                                  transform: `translateX(${viewMode === 'list' ? 0 : 100}%)` 
                                }}
                              />
                              <button
                                onClick={() => setViewMode('list')}
                                className={`relative z-10 flex items-center px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-500 ${
                                  viewMode === 'list' ? 'text-white' : 'text-white/40 hover:text-white/60'
                                }`}
                              >
                                <Squares2X2Icon className="h-4 w-4 mr-2" />
                                列表
                              </button>
                              <button
                                onClick={() => setViewMode('tree')}
                                className={`relative z-10 flex items-center px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-500 ${
                                  viewMode === 'tree' ? 'text-white' : 'text-white/40 hover:text-white/60'
                                }`}
                              >
                                <Bars3Icon className="h-4 w-4 mr-2" />
                                树状
                              </button>
                            </div>
                          </div>

                          <div className="flex flex-col w-64">
                            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 mb-2 px-1">
                              按模式删除
                            </label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                placeholder="例如: instance:*"
                                value={deletePattern}
                                onChange={(e) => setDeletePattern(e.target.value)}
                                className="flex-1 rounded-xl border-0 py-2 bg-white/5 text-white placeholder:text-white/20 focus:bg-white/10 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] outline-none ring-0 text-sm"
                              />
                              <Button
                                variant="destructive"
                                onClick={() => deleteByPattern(deletePattern)}
                                disabled={!deletePattern || loading}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                       {viewMode === 'list' ? (
                         <div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                            <InteractiveList
                              items={stats.samples.allKeys?.map(key => ({ id: key, key })) || []}
                              selectedItemId={showDetailPanel ? selectedKey : null}
                              onItemClick={(item) => handleViewDetails(item.key)}
                              renderItem={(item) => (
                                <div className="flex items-center justify-between">
                                 <div className="flex items-center min-w-0">
                                   <KeyIcon className={`h-4 w-4 mr-3 flex-shrink-0 transition-colors duration-500 ${
                                     selectedKey === item.key && showDetailPanel ? 'text-white' : 'text-white/20 group-hover:text-white/40'
                                   }`} />
                                   <span className={`text-sm font-mono truncate transition-colors duration-500 ${
                                     selectedKey === item.key && showDetailPanel ? 'text-white' : 'text-white/40 group-hover:text-white/70'
                                   }`}>{item.key}</span>
                                 </div>
                                 <div className="flex items-center space-x-3 ml-4">
                                   <button 
                                     className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-all"
                                     title="查看详情"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       handleViewDetails(item.key);
                                     }}
                                   >
                                     <InformationCircleIcon className="h-4 w-4" />
                                   </button>
                                   <input
                                     type="checkbox"
                                     checked={selectedKeys.includes(item.key)}
                                     onClick={(e) => e.stopPropagation()}
                                     onChange={(e) => {
                                       if (e.target.checked) {
                                         setSelectedKeys([...selectedKeys, item.key]);
                                       } else {
                                         setSelectedKeys(selectedKeys.filter(k => k !== item.key));
                                       }
                                     }}
                                     className="h-4 w-4 rounded border-white/20 bg-white/10 text-white focus:ring-white/30 focus:ring-offset-0 transition-all"
                                   />
                                 </div>
                               </div>
                             )}
                           />
                         </div>
                      ) : (
                        <div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                          <KeyTree 
                            keys={stats.samples.allKeys || []} 
                            onSelectKey={handleViewDetails}
                            selectedKey={selectedKey}
                          />
                        </div>
                      )}

                      {selectedKeys.length > 0 && (
                        <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
                          <span className="text-sm text-gray-400">
                            已选择 {selectedKeys.length} 个键
                          </span>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteKeys(selectedKeys)}
                          >
                            删除选中
                          </Button>
                        </div>
                      )}
                    </div>
                  </SpotlightCard>
                )}
              </TabPanel>

              {/* Operations Tab */}
              <TabPanel className="outline-none transition duration-300 ease-in-out data-closed:opacity-0 data-closed:-translate-y-1">
                {stats && (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <SpotlightCard variant="flat" className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                      <div className="p-6">
                        <h3 className="text-lg font-medium text-white mb-4">Key Distribution</h3>
                        <div className="h-64">
                          <KeyDistributionChart data={{
                            instanceKeys: stats.counts.instanceKeys,
                            taskKeys: stats.counts.taskKeys,
                            sessionKeys: stats.counts.sessionKeys,
                            otherKeys: stats.counts.totalKeys - stats.counts.instanceKeys - stats.counts.taskKeys - stats.counts.sessionKeys
                          }} />
                        </div>
                      </div>
                    </SpotlightCard>

                    <SpotlightCard variant="flat" className="overflow-hidden shadow-xl backdrop-blur-md" enablePhysics={true}>
                      <div className="p-6">
                        <h3 className="text-lg font-medium text-white mb-4">Quick Operations</h3>
                        <div className="space-y-4">
                          <button
                            onClick={() => setDeletePattern('instance:')}
                            className="w-full rounded-xl bg-white/5 px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/10 active:scale-[0.98] transition-all"
                          >
                            <div className="font-medium">清理过期实例</div>
                            <div className="text-sm text-gray-400">删除所有过期的实例键</div>
                          </button>
                          <button
                            onClick={() => setDeletePattern('task:')}
                            className="w-full rounded-xl bg-white/5 px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/10 active:scale-[0.98] transition-all"
                          >
                            <div className="font-medium">清理任务缓存</div>
                            <div className="text-sm text-gray-400">删除所有任务相关的缓存</div>
                          </button>
                          <button
                            onClick={() => loadCacheStats(true)}
                            className="w-full rounded-xl bg-white/10 px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/20 active:scale-[0.98] transition-all"
                          >
                            <div className="font-medium">刷新统计</div>
                            <div className="text-sm text-white/60">重新加载缓存统计信息</div>
                          </button>
                        </div>
                      </div>
                    </SpotlightCard>
                  </div>
                )}
              </TabPanel>
            </TabPanels>
          </TabGroup>
        )}
      </div>

      <KeyDetailPanel 
        keyName={showDetailPanel ? selectedKey : null}
        onClose={() => setShowDetailPanel(false)}
      />
    </div>
  );
}